import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "live", "pending_review", "ended", "sold", "rejected", "removed"];

const RemoveDialog = ({ target, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/listings/${target.id}`, { data: { reason } });
      toast.success("Listing removed", { description: `${r.data.bidders_notified ?? 0} bidders notified, ${r.data.watchers_cleared ?? 0} watchlists cleared.` });
      setReason(""); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-remove-listing-dialog">
        <DialogHeader><DialogTitle>Remove listing</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <b>{target?.title}</b> will be hidden from search, home, categories and watchlists. Active bids are cancelled. The record is kept for audit.
        </p>
        <Textarea data-testid="admin-remove-reason-input" placeholder="Reason (shown in audit log)" value={reason} onChange={e => setReason(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-remove-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-500 text-white" data-testid="admin-remove-confirm-btn">
            {busy ? "Removing…" : "Remove listing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminListings = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);

  const load = () => {
    const params = { limit: 100 };
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/listings", { params }).then(r => setRows(r.data)).catch(() => toast.error("Could not load listings"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="admin-all-listings">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <form onSubmit={e => { e.preventDefault(); load(); }} className="flex-1">
          <Input data-testid="admin-listings-search-input" placeholder="Search title or seller…" value={q} onChange={e => setQ(e.target.value)} />
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-48" data-testid="admin-listings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Seller</th><th className="px-4 py-3">Current</th><th className="px-4 py-3">Bids</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {rows.map(l => (
              <tr key={l.id} data-testid={`admin-listing-row-${l.id}`}>
                <td className="px-4 py-3 font-semibold"><Link to={`/auction/${l.id}`} className="hover:text-amber-600">{l.title}</Link></td>
                <td className="px-4 py-3">{l.seller_name}</td>
                <td className="px-4 py-3">{inr(l.current_bid || l.starting_bid)}</td>
                <td className="px-4 py-3">{l.bid_count}</td>
                <td className="px-4 py-3 text-xs font-bold uppercase" data-testid={`admin-listing-status-${l.id}`}>
                  <span className={l.status === "removed" ? "text-red-600" : ""}>{l.status.replace("_", " ")}</span>
                  {l.removal_reason && <div className="font-normal normal-case text-slate-500">{l.removal_reason}</div>}
                </td>
                <td className="px-4 py-3">
                  {l.status !== "removed" && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setTarget(l)} data-testid={`admin-remove-listing-btn-${l.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No listings found.</td></tr>}
          </tbody>
        </table>
      </div>
      <RemoveDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); onChanged?.(); }} />
    </div>
  );
};
